import type { RequestContext } from "../context/request-context.js";
import type { AuditRecorder } from "../../audit/audit-recorder.js";
import { AuditAction } from "../../audit/audit-log.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import type { EquipmentService } from "../../equipment/equipment-service.js";
import type { Equipment } from "../../equipment/equipment-domain.js";
import { sha256Hex } from "./document-storage-service.js";
import { findSheetName, findSheetNames, type SpreadsheetWorkbook } from "../../domain/import/spreadsheet.js";
import { readWorkbook } from "../../infrastructure/import/xlsx-reader.js";
import { readPdfAsWorkbook, PdfNoTextLayerError } from "../../infrastructure/import/pdf-reader.js";
import {
  mapSystemsSheet,
  type ImportMappingOptions,
  mapPipingSheet,
  mergePipingDrafts,
  validateSystemLinks,
  type ImportIssue,
  type ImportedEquipmentDraft
} from "../../domain/import/equipment-import.js";
import {
  compareWithExisting,
  complementPatch,
  validateTechnicalCoherence,
  directExtraction,
  applyCorrection,
  isConfirmable,
  isBlockingExtraction,
  type StagingRecord,
  type StagingDisposition,
  type FieldExtraction
} from "../../domain/import/import-staging.js";
import {
  previousRunsOf,
  type ImportBatch,
  type ImportBatchRepository,
  type ImportSourceFormat
} from "../../domain/import/import-batch.js";

const randomUUID = () => globalThis.crypto.randomUUID();

/**
 * CONTEXTO OBRIGATÓRIO da importação.
 * ARQUIVO → CLIENTE → OS → PROJETO → UNIDADE/ÁREA.
 */
export interface ImportContext {
  readonly clientId: string;
  readonly serviceOrderId: string;
  readonly projectId: string;
  readonly unitAreaId: string;
}

/** O arquivo cru. O serviço decide o formato e calcula o hash. */
export interface ImportSource {
  readonly filename: string;
  readonly bytes: Uint8Array;
}

/** Correção manual de um campo, feita no staging antes da confirmação. */
export interface ManualCorrection {
  readonly rowNumber: number;
  readonly field: string;
  readonly correctedValue: string;
}

export interface ImportSheetSummary {
  readonly sheetName: string | null;
  readonly total: number;
  readonly novos: number;
  readonly existentes: number;
  readonly conflitos: number;
  readonly bloqueados: number;
  readonly missingColumns: readonly string[];
  readonly unmappedColumns: readonly string[];
}

export interface ImportPreview {
  readonly sourceFilename: string;
  readonly sourceHash: string;
  readonly sourceFormat: ImportSourceFormat;
  readonly systems: ImportSheetSummary;
  readonly piping: ImportSheetSummary;
  readonly records: readonly StagingRecord[];
  readonly issues: readonly ImportIssue[];
  readonly errorCount: number;
  readonly warningCount: number;
  /** Lotes anteriores confirmados com o MESMO arquivo e o MESMO contexto. */
  readonly previousRuns: readonly ImportBatch[];
  /** Só é verdadeiro quando tudo o que o comando exige está satisfeito. */
  readonly canConfirm: boolean;
  readonly blockingReasons: readonly string[];
}

export interface ImportOutcome {
  readonly batchId: string;
  readonly createdSystems: number;
  readonly createdPiping: number;
  readonly reusedSystems: number;
  readonly reusedPiping: number;
  /**
   * Cadastros JÁ existentes que este lote COMPLETOU (backfill). Contado à
   * parte de `reused`: "reaproveitei sem mexer" e "completei o que faltava"
   * são resultados diferentes, e quem reimporta para corrigir cadastro
   * precisa ver o segundo.
   */
  readonly complementedSystems: number;
  readonly complementedPiping: number;
  /** Nomes dos campos completados, sem repetição — o que mudou, de fato. */
  readonly complementedFields: readonly string[];
  readonly skipped: number;
  readonly failures: readonly ImportIssue[];
  readonly rolledBack: boolean;
}

export interface ImportExecuteOptions {
  /** Linhas que o usuário retirou do lote no Preview. */
  readonly excludedRows?: readonly number[];
  readonly corrections?: readonly ManualCorrection[];
  /** Documento do arquivo original na Biblioteca Técnica (Bloco 03). */
  readonly sourceDocumentId?: string | null;
}

/** Remoção usada apenas na reversão compensatória de um lote que falhou. */
export interface EquipmentRemovalPort {
  remove(clientId: string, equipmentId: string): Promise<void>;
}

interface ParsedSource {
  readonly workbook: SpreadsheetWorkbook;
  readonly format: ImportSourceFormat;
  readonly hash: string;
}

/**
 * IMPORTAÇÃO INTELIGENTE DE SISTEMAS E TUBULAÇÕES (Bloco 04).
 *
 * Um único caminho para XLSX e PDF:
 *   arquivo → leitor do formato → MESMO modelo intermediário → validação →
 *   staging → preview → confirmação → EquipmentService.createEquipment.
 *
 * Nada é gravado antes da confirmação. Durante a gravação, uma falha crítica
 * reverte tudo o que o lote criou, para que nenhuma Tubulação fique órfã.
 */
export class EquipmentImportService {
  private readonly auth = new RbacAuthorizationService();
  /** CONFIGURAÇÕES → IMPORTAÇÕES (aliases explícitos e marcadores de vazio). Sem ele, o mapeamento de sempre. */
  private mappingOptions: (() => Promise<ImportMappingOptions>) | null = null;

  attachMappingOptions(provider: () => Promise<ImportMappingOptions>): void {
    this.mappingOptions = provider;
  }

  constructor(
    private readonly equipmentService: EquipmentService,
    private readonly batchRepo: ImportBatchRepository,
    private readonly removalPort?: EquipmentRemovalPort,
    private readonly audit?: AuditRecorder
  ) {}

  // ------------------------------------------------------------------ leitura

  private async parseSource(source: ImportSource): Promise<ParsedSource> {
    const hash = await sha256Hex(this.asLatin1(source.bytes));
    const isPdf = /\.pdf$/i.test(source.filename) || this.looksLikePdf(source.bytes);

    if (isPdf) {
      try {
        return { workbook: await readPdfAsWorkbook(source.bytes), format: "PDF_TEXT", hash };
      } catch (error) {
        if (error instanceof PdfNoTextLayerError) {
          throw new AppError(ErrorCode.Validation, "PDF without text layer", {
            safeMessage:
              "Este PDF é digitalizado e não tem camada de texto. O reconhecimento óptico (OCR) não está disponível nesta instalação, " +
              "então ele não pode ser importado. Envie o PDF pesquisável ou a planilha .xlsx equivalente."
          });
        }
        throw new AppError(ErrorCode.Validation, "PDF unreadable", {
          safeMessage: (error as Error)?.message || "Não foi possível ler o PDF."
        });
      }
    }

    return { workbook: await readWorkbook(source.bytes), format: "XLSX", hash };
  }

  private looksLikePdf(bytes: Uint8Array): boolean {
    return bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46;
  }

  private asLatin1(bytes: Uint8Array): string {
    let out = "";
    for (let i = 0; i < bytes.length; i++) out += String.fromCharCode(bytes[i]!);
    return out;
  }

  // ------------------------------------------------------------------ staging

  /**
   * Lê, critica e monta o STAGING. Nenhum Equipment é criado aqui — é esta a
   * camada intermediária que o comando exige antes da confirmação.
   */
  async preview(
    context: RequestContext,
    source: ImportSource,
    target: ImportContext,
    options: ImportExecuteOptions = {}
  ): Promise<ImportPreview> {
    this.auth.require(context, PermissionCode.EquipmentImport);
    this.requireContext(target);

    const parsed = await this.parseSource(source);
    const systemsSheet = findSheetName(parsed.workbook, "Sistemas", "Sistema", "Systems");
    /**
     * TODAS as abas de tubulação, não só a primeira.
     *
     * A planilha cadastral real descreve cada linha em duas tabelas —
     * processo e materiais —, que exportadas viram abas como
     * "Tubulações - Processo" e "Tubulações - Materiais". Lendo uma só, a
     * outra era ignorada em silêncio e metade do cadastro técnico se perdia.
     */
    const pipingSheets = findSheetNames(parsed.workbook, "Tubulações", "Tubulacoes", "Linhas", "Piping");
    const pipingSheet = pipingSheets[0] ?? null;
    if (!systemsSheet && !pipingSheet) {
      throw new AppError(ErrorCode.Validation, "No importable sheet", {
        safeMessage: `O arquivo não tem seção "Sistemas" nem "Tubulações". Seções encontradas: ${parsed.workbook.sheetNames.join(", ") || "nenhuma"}.`
      });
    }

    const empty = { drafts: [] as ImportedEquipmentDraft[], issues: [] as ImportIssue[], missingColumns: [] as string[], unmappedColumns: [] as string[] };
    const systemsTable = systemsSheet ? parsed.workbook.table(systemsSheet) : null;
    const mappingOptions = this.mappingOptions ? await this.mappingOptions() : {};
    const systemsResult = systemsTable ? mapSystemsSheet(systemsTable, mappingOptions) : empty;

    // Cada aba de tubulação é mapeada e depois MESCLADA por Sistema + TAG:
    // a mesma linha descrita em duas tabelas vira um cadastro só, sem que o
    // campo vazio de uma apague o que a outra trouxe.
    const perSheet = pipingSheets
      .map(name => parsed.workbook.table(name))
      .filter((table): table is NonNullable<typeof table> => table !== null)
      .map(table => mapPipingSheet(table, mappingOptions));

    const mergedPiping = mergePipingDrafts(perSheet.flatMap(result => result.drafts));
    const pipingResult = {
      drafts: mergedPiping.drafts,
      issues: [...perSheet.flatMap(r => r.issues), ...mergedPiping.issues],
      // Uma coluna só é "faltante" se faltar em TODAS as abas: a aba de
      // materiais não tem as colunas de processo, e vice-versa.
      missingColumns: perSheet.length === 0 ? [] :
        perSheet[0]!.missingColumns.filter(column => perSheet.every(r => r.missingColumns.includes(column))),
      unmappedColumns: [...new Set(perSheet.flatMap(r => r.unmappedColumns))]
    };

    // Equipamentos já cadastrados NO MESMO CONTEXTO — base da idempotência.
    const existingAll = await this.equipmentService.listEquipments(context, { status: "ALL", clientId: target.clientId });
    const inContext = existingAll.filter(equipment => this.sameContext(equipment, target));
    const existingByTag = new Map(inContext.map(equipment => [equipment.tag.trim().toUpperCase(), equipment]));
    const outOfContextByTag = new Map(
      existingAll.filter(equipment => !this.sameContext(equipment, target)).map(e => [e.tag.trim().toUpperCase(), e])
    );

    const linkIssues = validateSystemLinks(
      pipingResult.drafts,
      systemsResult.drafts.map(d => d.tag),
      inContext.filter(e => e.equipmentType === "PIPING_SYSTEM").map(e => e.tag)
    );

    const issues: ImportIssue[] = [...systemsResult.issues, ...pipingResult.issues, ...linkIssues];
    const issuesByRow = new Map<number, ImportIssue[]>();
    for (const issue of issues) {
      const list = issuesByRow.get(issue.rowNumber) ?? [];
      list.push(issue);
      issuesByRow.set(issue.rowNumber, list);
    }

    const excluded = new Set(options.excludedRows ?? []);
    const correctionsByRow = new Map<number, ManualCorrection[]>();
    for (const correction of options.corrections ?? []) {
      const list = correctionsByRow.get(correction.rowNumber) ?? [];
      list.push(correction);
      correctionsByRow.set(correction.rowNumber, list);
    }

    const now = new Date().toISOString();
    const userId = context.userId ? String(context.userId) : "system";
    const allDrafts = [...systemsResult.drafts, ...pipingResult.drafts];
    const records: StagingRecord[] = allDrafts.map(draft =>
      this.toStagingRecord(draft, {
        rowIssues: issuesByRow.get(draft.rowNumber) ?? [],
        existingByTag,
        outOfContextByTag,
        excluded,
        corrections: correctionsByRow.get(draft.rowNumber) ?? [],
        userId,
        now
      })
    );

    // Divergências técnicas viram avisos visíveis, sem corrigir nada.
    for (const record of records) {
      for (const divergence of validateTechnicalCoherence(record.draft)) {
        issues.push({ rowNumber: record.rowNumber, column: null, severity: "WARNING", message: divergence.message });
      }
    }

    const previousRuns = previousRunsOf(await this.batchRepo.list({ sourceHash: parsed.hash }), parsed.hash, target);
    const blockingReasons = this.blockingReasons(records);

    return {
      sourceFilename: source.filename,
      sourceHash: parsed.hash,
      sourceFormat: parsed.format,
      systems: this.summarize(systemsSheet, records.filter(r => r.kind === "PIPING_SYSTEM"), systemsResult),
      piping: this.summarize(pipingSheet, records.filter(r => r.kind === "PIPING_LINE"), pipingResult),
      records,
      issues,
      errorCount: issues.filter(i => i.severity === "ERROR").length,
      warningCount: issues.filter(i => i.severity === "WARNING").length,
      previousRuns,
      canConfirm: blockingReasons.length === 0 && records.some(r => isConfirmable(r) && r.disposition === "NOVO"),
      blockingReasons
    };
  }

  private sameContext(equipment: Equipment, target: ImportContext): boolean {
    return String(equipment.clientId) === target.clientId
      && String(equipment.serviceOrderId ?? "") === target.serviceOrderId
      && String(equipment.projectId ?? "") === target.projectId
      && String(equipment.unitAreaId ?? "") === target.unitAreaId;
  }

  private toStagingRecord(
    draft: ImportedEquipmentDraft,
    input: {
      rowIssues: ImportIssue[];
      existingByTag: Map<string, Equipment>;
      outOfContextByTag: Map<string, Equipment>;
      excluded: Set<number>;
      corrections: ManualCorrection[];
      userId: string;
      now: string;
    }
  ): StagingRecord {
    const key = draft.tag.trim().toUpperCase();
    const existing = input.existingByTag.get(key) ?? null;
    const outOfContext = input.outOfContextByTag.get(key) ?? null;

    let extractions: FieldExtraction[] = [
      directExtraction("tag", draft.tag),
      directExtraction("systemTag", draft.systemTag)
    ];
    for (const correction of input.corrections) {
      extractions = extractions.map(extraction =>
        extraction.field === correction.field
          ? applyCorrection(extraction, correction.correctedValue, input.userId, input.now)
          : extraction
      );
    }

    const issues = [...input.rowIssues];
    let disposition: StagingDisposition;
    let conflicts: StagingRecord["conflicts"] = [];

    if (issues.some(i => i.severity === "ERROR")) {
      disposition = "BLOQUEADO";
    } else if (existing) {
      const comparison = compareWithExisting(draft, existing as unknown as Record<string, unknown>);
      disposition = comparison.disposition;
      conflicts = comparison.conflicts;
    } else if (outOfContext) {
      // A TAG existe no mesmo Cliente, porém em OUTRO Projeto/OS. O cadastro
      // atual exige TAG única por Cliente, então a gravação seria recusada.
      // O registro é bloqueado com a explicação, em vez de falhar na gravação.
      disposition = "BLOQUEADO";
      issues.push({
        rowNumber: draft.rowNumber, column: "TAG", severity: "ERROR",
        message: `TAG "${draft.tag}" já existe neste Cliente em outro contexto (Projeto/OS). ` +
          "O cadastro exige TAG única por Cliente, então este registro não pode ser criado aqui."
      });
    } else {
      disposition = "NOVO";
    }

    return {
      id: `stg-${draft.kind}-${draft.rowNumber}`,
      rowNumber: draft.rowNumber,
      kind: draft.kind,
      tag: draft.tag,
      systemTag: draft.systemTag,
      draft,
      disposition,
      existingEquipmentId: existing ? String(existing.id) : null,
      conflicts,
      extractions,
      issues,
      selected: !input.excluded.has(draft.rowNumber)
    };
  }

  private summarize(
    sheetName: string | null,
    records: readonly StagingRecord[],
    mapping: { missingColumns: readonly string[]; unmappedColumns: readonly string[] }
  ): ImportSheetSummary {
    return {
      sheetName,
      total: records.length,
      novos: records.filter(r => r.disposition === "NOVO").length,
      existentes: records.filter(r => r.disposition === "EXISTENTE / SEM ALTERAÇÃO" || r.disposition === "COMPLEMENTAÇÃO").length,
      conflitos: records.filter(r => r.disposition === "CONFLITO").length,
      bloqueados: records.filter(r => r.disposition === "BLOQUEADO").length,
      missingColumns: mapping.missingColumns,
      unmappedColumns: mapping.unmappedColumns
    };
  }

  /** Por que CONFIRMAR IMPORTAÇÃO ainda não pode ser habilitado. */
  private blockingReasons(records: readonly StagingRecord[]): string[] {
    const reasons: string[] = [];
    const selected = records.filter(r => r.selected);

    if (selected.some(r => r.disposition === "CONFLITO")) {
      reasons.push("Há conflito pendente entre o valor cadastrado e o valor do arquivo. Resolva ou retire a linha do lote.");
    }
    if (selected.some(r => r.disposition === "BLOQUEADO")) {
      reasons.push("Há linha bloqueada por erro. Corrija o arquivo ou retire a linha do lote.");
    }
    if (selected.some(r => r.extractions.some(isBlockingExtraction))) {
      reasons.push("Há campo essencial com baixa confiança de extração. Corrija o valor antes de confirmar.");
    }

    // Nenhuma Tubulação pode entrar sem o seu Sistema.
    const systemTags = new Set(
      records.filter(r => r.kind === "PIPING_SYSTEM" && r.disposition !== "BLOQUEADO").map(r => r.tag.trim().toUpperCase())
    );
    const orphan = selected.some(record =>
      record.kind === "PIPING_LINE" &&
      record.disposition === "NOVO" &&
      record.systemTag !== null &&
      !systemTags.has(record.systemTag.trim().toUpperCase()) &&
      record.existingEquipmentId === null &&
      !record.issues.some(i => i.severity === "ERROR")
    );
    if (orphan) reasons.push("Há Tubulação sem Sistema correspondente no lote nem no cadastro.");

    return reasons;
  }

  // ---------------------------------------------------------------- gravação

  /**
   * Confirma o lote. Só grava o que está selecionado, é NOVO e passou em
   * tudo. Uma falha crítica durante a gravação REVERTE o lote inteiro.
   */
  async execute(
    context: RequestContext,
    source: ImportSource,
    target: ImportContext,
    options: ImportExecuteOptions = {}
  ): Promise<ImportOutcome> {
    this.auth.require(context, PermissionCode.EquipmentImport);
    this.requireContext(target);

    const startedAt = Date.now();
    const preview = await this.preview(context, source, target, options);

    if (preview.blockingReasons.length > 0) {
      throw new AppError(ErrorCode.Validation, "Import blocked", {
        safeMessage: `A importação não pode ser confirmada: ${preview.blockingReasons.join(" ")}`
      });
    }

    const batchId = `imp-${randomUUID()}`;
    const userId = context.userId ? String(context.userId) : "system";
    const toCreate = preview.records.filter(record => isConfirmable(record) && record.disposition === "NOVO");
    const systems = toCreate.filter(r => r.kind === "PIPING_SYSTEM");
    const lines = toCreate.filter(r => r.kind === "PIPING_LINE");

    const createdIds: Array<{ clientId: string; id: string }> = [];
    const failures: ImportIssue[] = [];
    let rolledBack = false;
    /** Cadastros JÁ existentes que este lote completou (backfill). */
    let complemented: Array<{ id: string; kind: string; fields: string[] }> = [];

    try {
      // Bloco 04 (correção de escala): sem isto, o repositório em memória
      // regrava as tabelas inteiras no localStorage a cada UM dos milhares
      // de registros — O(n²) de bytes escritos, lento o bastante para travar
      // a aba e cedo o bastante para estourar a cota do navegador bem antes
      // do lote terminar. O lote grava uma única vez, ao final.
      //
      // `endBulkOperation` precisa rodar SEMPRE, mesmo se o laço lançar no
      // meio — senão o lote fica "aberto" para sempre e toda gravação futura
      // do app (não só de importação) para de persistir silenciosamente.
      // Por isso o `finally`: ele fecha o lote em qualquer saída, e só then
      // o erro do laço (se houver) continua para o catch de fora, que decide
      // a reversão.
      this.equipmentService.beginBulkOperation();
      let loopSucceeded = false;
      try {
        // Sistemas primeiro: a Tubulação resolve o seu Sistema pela TAG gravada.
        for (const record of [...systems, ...lines]) {
          const equipment = this.toEquipment(record, target, userId, batchId);
          const saved = await this.equipmentService.createEquipment(context, equipment);
          createdIds.push({ clientId: target.clientId, id: String(saved.id) });
        }
        complemented = await this.applyComplements(context, preview, target);
        loopSucceeded = true;
      } finally {
        // Propaga erro de armazenamento/transação (cota excedida, ou um
        // COMMIT que não pôde ser confirmado) em vez de engolir: sem isto,
        // o lote "concluiria com sucesso" mas os registros não
        // sobreviveriam a um F5 — falha silenciosa proibida pelo comando.
        // Se o laço já falhou, fecha com commit=false — em repositórios com
        // transação real (SQLite) isso é um ROLLBACK de verdade, que já
        // desfaz tudo sozinho; a reversão compensatória abaixo continua
        // rodando de qualquer forma, e é inofensiva sobre dado que já não
        // existe mais. Em JS um erro do `finally` substitui o do `try`; nos
        // dois casos o catch de fora recebe um erro real e aciona a reversão.
        await this.equipmentService.endBulkOperation(loopSucceeded);
      }
    } catch (error) {
      // Falha crítica: desfaz o que este lote criou. Sem isto, uma Tubulação
      // poderia ficar sem o seu Sistema, ou o lote ficaria pela metade.
      rolledBack = await this.rollback(createdIds);
      const message = (error as { safeMessage?: string })?.safeMessage || (error as Error)?.message || "Falha ao gravar o lote.";
      failures.push({ rowNumber: 0, column: null, severity: "ERROR", message });

      await this.saveBatch(batchId, source, preview, target, userId, {
        status: rolledBack ? "REVERTIDO" : "FALHOU",
        createdSystems: 0,
        createdPiping: 0,
        createdEquipmentIds: rolledBack ? [] : createdIds.map(c => c.id),
        corrections: (options.corrections ?? []).length,
        processingDuration: Date.now() - startedAt,
        sourceDocumentId: options.sourceDocumentId ?? null,
        failureReason: message
      });

      throw new AppError(ErrorCode.Conflict, "Import failed", {
        safeMessage: rolledBack
          ? `A importação falhou e foi revertida por inteiro, para não deixar cadastro pela metade. Motivo: ${message}`
          : `A importação falhou e a reversão automática não pôde ser concluída. Motivo: ${message}`
      });
    }

    const reused = preview.records.filter(r => r.disposition === "EXISTENTE / SEM ALTERAÇÃO" || r.disposition === "COMPLEMENTAÇÃO");
    const skipped = preview.records.length - toCreate.length - reused.length;

    await this.saveBatch(batchId, source, preview, target, userId, {
      status: "CONFIRMADO",
      createdSystems: systems.length,
      createdPiping: lines.length,
      createdEquipmentIds: createdIds.map(c => c.id),
      corrections: (options.corrections ?? []).length,
      processingDuration: Date.now() - startedAt,
      sourceDocumentId: options.sourceDocumentId ?? null,
      failureReason: null
    });

    await this.recordAudit(context, batchId, target, {
      createdSystems: systems.length,
      createdPiping: lines.length,
      sourceHash: preview.sourceHash,
      sourceFormat: preview.sourceFormat
    });

    return {
      batchId,
      createdSystems: systems.length,
      createdPiping: lines.length,
      reusedSystems: reused.filter(r => r.kind === "PIPING_SYSTEM").length,
      reusedPiping: reused.filter(r => r.kind === "PIPING_LINE").length,
      complementedSystems: complemented.filter(c => c.kind === "PIPING_SYSTEM").length,
      complementedPiping: complemented.filter(c => c.kind === "PIPING_LINE").length,
      complementedFields: [...new Set(complemented.flatMap(c => c.fields))].sort(),
      skipped,
      failures,
      rolledBack: false
    };
  }

  /**
   * BACKFILL — completa os cadastros que já existiam.
   *
   * Até aqui, um registro marcado `COMPLEMENTAÇÃO` era detectado, mostrado
   * no Preview ("preenche Material base, Categoria NR-13…") e **descartado
   * na confirmação**: entrava na contagem de "reaproveitados" e nada era
   * gravado. Quem reimportava a planilha corrigida via a promessa na tela e
   * o cadastro continuava incompleto.
   *
   * Isto é o que torna possível completar as Tubulações importadas antes da
   * correção do mapeamento (CLAUDE.md §42): a planilha de origem não é
   * guardada em lugar nenhum, então reimportá-la é o único caminho — e ele
   * precisa completar, não apenas relatar.
   *
   * SÓ PREENCHE CAMPO VAZIO. Registro em `CONFLITO` não é tocado nem nos
   * campos vazios dele: divergência é decisão do usuário, e completar
   * metade de um registro cuja outra metade está em disputa deixaria o
   * cadastro num estado que ninguém aprovou.
   */
  private async applyComplements(
    context: RequestContext,
    preview: ImportPreview,
    target: ImportContext
  ): Promise<Array<{ id: string; kind: string; fields: string[] }>> {
    const applied: Array<{ id: string; kind: string; fields: string[] }> = [];
    const pending = preview.records.filter(r =>
      r.selected && r.disposition === "COMPLEMENTAÇÃO" && r.existingEquipmentId);
    if (pending.length === 0) return applied;

    const all = await this.equipmentService.listEquipments(context, { status: "ALL", clientId: target.clientId });
    const byId = new Map(all.map(equipment => [String(equipment.id), equipment]));

    for (const record of pending) {
      const existing = byId.get(String(record.existingEquipmentId));
      if (!existing) continue;
      const patch = complementPatch(record.draft, existing as unknown as Record<string, unknown>);
      const fields = Object.keys(patch);
      if (fields.length === 0) continue;
      await this.equipmentService.updateEquipment(context, { ...existing, ...patch } as typeof existing);
      applied.push({ id: String(existing.id), kind: record.kind, fields });
    }
    return applied;
  }

  /** Reversão compensatória: o repositório não oferece transação real. */
  private async rollback(created: ReadonlyArray<{ clientId: string; id: string }>): Promise<boolean> {
    if (!this.removalPort) return false;
    try {
      // Ordem inversa: as Tubulações saem antes dos Sistemas.
      for (const item of [...created].reverse()) {
        await this.removalPort.remove(item.clientId, item.id);
      }
      return true;
    } catch (error) {
      console.warn("[IMPORTAÇÃO] Falha ao reverter o lote", error);
      return false;
    }
  }

  private async saveBatch(
    batchId: string,
    source: ImportSource,
    preview: ImportPreview,
    target: ImportContext,
    userId: string,
    result: {
      status: ImportBatch["status"];
      createdSystems: number;
      createdPiping: number;
      createdEquipmentIds: readonly string[];
      corrections: number;
      processingDuration: number;
      sourceDocumentId: string | null;
      failureReason: string | null;
    }
  ): Promise<void> {
    const batch: ImportBatch = {
      id: batchId,
      sourceDocumentId: result.sourceDocumentId,
      sourceFilename: source.filename,
      sourceHash: preview.sourceHash,
      sourceFormat: preview.sourceFormat,
      createdBy: userId,
      createdAt: new Date().toISOString(),
      clientId: target.clientId,
      serviceOrderId: target.serviceOrderId,
      projectId: target.projectId,
      unitAreaId: target.unitAreaId,
      status: result.status,
      systemsFound: preview.systems.total,
      systemsCreated: result.createdSystems,
      systemsReused: preview.systems.existentes,
      pipelinesFound: preview.piping.total,
      pipelinesCreated: result.createdPiping,
      pipelinesReused: preview.piping.existentes,
      corrections: result.corrections,
      warnings: preview.warningCount,
      errors: preview.errorCount,
      processingDuration: result.processingDuration,
      createdEquipmentIds: result.createdEquipmentIds,
      failureReason: result.failureReason
    };
    await this.batchRepo.save(batch);
  }

  /** Consulta dos lotes — a rastreabilidade exigida é persistente e legível. */
  async listBatches(context: RequestContext, filter: Parameters<ImportBatchRepository["list"]>[0] = {}): Promise<ImportBatch[]> {
    this.auth.require(context, PermissionCode.EquipmentRead);
    return this.batchRepo.list(filter);
  }

  async getBatch(context: RequestContext, batchId: string): Promise<ImportBatch | null> {
    this.auth.require(context, PermissionCode.EquipmentRead);
    return this.batchRepo.findById(batchId);
  }

  // ------------------------------------------------------------------ apoio

  private requireContext(target: ImportContext): void {
    if (!target?.clientId?.trim()) {
      throw new AppError(ErrorCode.Validation, "Client required", { safeMessage: "Selecione o Cliente de destino da importação." });
    }
    if (!target.serviceOrderId?.trim()) {
      throw new AppError(ErrorCode.Validation, "ServiceOrder required", { safeMessage: "Selecione a Ordem de Serviço (OS) de destino da importação." });
    }
    if (!target.projectId?.trim()) {
      throw new AppError(ErrorCode.Validation, "Project required", { safeMessage: "Selecione o Projeto de destino da importação." });
    }
    if (!target.unitAreaId?.trim()) {
      throw new AppError(ErrorCode.Validation, "UnitArea required", { safeMessage: "Selecione a Unidade/Área de destino da importação." });
    }
  }

  /** Converte o registro do staging em Equipment. Campo ausente vira null. */
  private toEquipment(record: StagingRecord, target: ImportContext, userId: string, batchId: string): Equipment {
    const draft = record.draft;
    const now = new Date().toISOString();
    const isPiping = draft.kind === "PIPING_LINE";

    const measurementOrigin = (measurement: typeof draft.designPressure) =>
      measurement ? { value: measurement.originalValue, unit: measurement.originalUnit, recognized: measurement.recognized } : null;

    const payload = {
      // `source` é a única chave que EquipmentService preserva ao reconstruir
      // o payloadJson — é onde a procedência do lote sobrevive.
      source: {
        importBatchId: batchId,
        importedFrom: record.draft.kind,
        importedAt: now,
        sourceRow: draft.rowNumber,
        sourceColumns: draft.extras,
        // Unidades como vieram no arquivo, antes da normalização.
        originalUnits: {
          designPressure: measurementOrigin(draft.designPressure),
          operatingPressure: measurementOrigin(draft.operatingPressure),
          designTemperature: measurementOrigin(draft.designTemperature),
          operatingTemperature: measurementOrigin(draft.operatingTemperature),
          nominalThickness: measurementOrigin(draft.nominalThickness),
          minimumThickness: measurementOrigin(draft.minimumThickness)
        },
        corrections: record.extractions
          .filter(extraction => extraction.status === "CORRIGIDO PELO USUÁRIO")
          .map(extraction => ({
            field: extraction.field,
            extractedValue: extraction.extractedValue,
            correctedValue: extraction.correctedValue,
            correctedBy: extraction.correctedBy,
            correctedAt: extraction.correctedAt,
            page: extraction.page
          }))
      }
    };

    const measure = (m: typeof draft.designPressure) => (m ? { value: m.value, unit: m.unit } : null);

    return {
      id: `EQ-${randomUUID()}` as Equipment["id"],
      clientId: target.clientId as Equipment["clientId"],
      serviceOrderId: target.serviceOrderId as Equipment["serviceOrderId"],
      projectId: target.projectId,
      unitAreaId: target.unitAreaId,
      areaId: target.unitAreaId as Equipment["areaId"],
      tag: draft.tag,
      name: draft.name,
      description: draft.service,
      equipmentType: draft.kind,
      system: draft.systemTag,
      pipeSystemApplicability: isPiping ? "APPLICABLE" : undefined,
      pipingSystemId: draft.systemTag,
      subsystem: null,
      service: draft.service,
      manufacturer: null,
      model: null,
      serialNumber: null,
      yearOfManufacture: null,
      installationDate: null,
      commissioningDate: null,
      criticality: null,
      location: null,
      notes: null,
      status: "ACTIVE",
      lineNumber: draft.lineNumber,
      fromReference: draft.fromReference,
      toReference: draft.toReference,
      fluid: draft.fluid,
      fluidCode: draft.fluidCode,
      fluidClass: draft.fluidClass,
      designPressure: measure(draft.designPressure),
      designTemperature: measure(draft.designTemperature),
      operatingPressure: measure(draft.operatingPressure),
      operatingTemperature: measure(draft.operatingTemperature),
      pmtaValue: draft.pmtaValue,
      pmtaUnit: draft.pmtaUnit,
      material: draft.material,
      nominalThickness: measure(draft.nominalThickness),
      // Referência cadastral da planilha, com a origem declarada. Não é
      // cálculo de projeto nem FFS, e a sobre-espessura de corrosão NÃO é
      // derivada daqui — seria dado de engenharia inventado.
      minimumRequiredThickness: draft.minimumThickness
        ? { value: draft.minimumThickness.value, unit: draft.minimumThickness.unit, source: "MANUAL", calculationBasis: "Importação cadastral (arquivo do cliente)" }
        : null,
      /**
       * Campos da Lista de Linhas real (CLAUDE.md §42.1). Eles passaram a
       * ser capturados no rascunho, mas NÃO eram gravados no Equipment:
       * eram lidos da planilha e descartados na criação. Todos já existiam
       * em `Equipment`; o que faltava era escrevê-los.
       */
      outsideDiameter: measure(draft.outsideDiameter ?? null),
      corrosionAllowance: measure(draft.corrosionAllowance ?? null),
      materialFamily: draft.materialFamily ?? null,
      fabricationStandard: draft.fabricationStandard ?? null,
      dimensionalStandard: draft.dimensionalStandard ?? null,
      nps: draft.nps,
      schedule: draft.schedule,
      spec: draft.spec,
      pressureClass: draft.pressureClass,
      drawingReference: draft.drawingReference,
      flowDiagramReference: draft.flowDiagramReference,
      // Legado combinado: mantido para os pontos do app que ainda só leem
      // este campo (ex.: seção "DADOS DE PROJETO" do RI para outros tipos de
      // Equipamento) — os campos oficiais separados acima são a fonte real.
      projectDrawingFlowchart: draft.flowDiagramReference ?? draft.drawingReference,
      nr13Category: draft.nr13Category,
      // A categoria vem de uma base cadastral, não de um ato técnico. Ela
      // entra como NÃO CLASSIFICADA até validação do PLH, preservando o
      // mecanismo existente em vez de declarar a classificação definitiva.
      nr13Applicability: "UNCLASSIFIED",
      insulation: null,
      payloadJson: JSON.stringify(payload),
      version: 1,
      createdAt: now,
      createdBy: userId,
      updatedAt: now
    } as Equipment;
  }

  private async recordAudit(context: RequestContext, batchId: string, target: ImportContext, metadata: Record<string, unknown>): Promise<void> {
    if (!this.audit) return;
    try {
      await this.audit.record(context, {
        action: AuditAction.EquipmentImportExecuted,
        entityType: "EquipmentImport",
        entityId: batchId,
        previousState: null,
        newState: null,
        metadata: { ...target, ...metadata } as Record<string, unknown>
      } as Parameters<AuditRecorder["record"]>[1]);
    } catch (error) {
      console.warn("[IMPORTAÇÃO] Falha ao registrar auditoria", error);
    }
  }
}
